import React, { useCallback } from "react";
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	CircularProgress,
	TablePagination,
} from "@mui/material";
import { EstoqueService } from "../../service/estoque_service";
import useEstoqueLista from "./useEstoqueLista";
import { formatDate, formatNumber } from "./formatters";

/**
 * Estoque de entradas: lotes consolidados que entraram e ainda não foram
 * processados em nenhuma ordem de produção. Lote que já teve qualquer parte
 * puxada por uma OP sai daqui e passa a ser acompanhado pela OP.
 */
function EstoqueEntradas({ unidadeDistribuidoraId, searchTerm }) {
	const fetcher = useCallback(
		(unidadeId, search) => new EstoqueService().getEstoqueEntradas(unidadeId, search),
		[]
	);

	const {
		itens,
		itensPaginados,
		loading,
		page,
		rowsPerPage,
		handleChangePage,
		handleChangeRowsPerPage,
	} = useEstoqueLista(fetcher, unidadeDistribuidoraId, searchTerm);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<CircularProgress sx={{ color: "#1a9e8f" }} />
			</Box>
		);
	}

	if (itens.length === 0) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "400px",
					gap: 2,
					p: 4,
				}}
			>
				<Typography variant="h6" sx={{ color: "#999", textAlign: "center" }}>
					Nenhuma entrada em estoque
				</Typography>
				<Typography variant="body2" sx={{ color: "#999", textAlign: "center" }}>
					{searchTerm
						? "Tente fazer uma busca diferente"
						: "Não há entradas aguardando processamento em ordem de produção"}
				</Typography>
			</Box>
		);
	}

	return (
		<>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow sx={{ bgcolor: "#f5f5f5" }}>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>Data</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>Produto</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>Qualidade</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>
								Código de Rastreio
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>Fornecedor</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }} align="right">
								Quantidade em Estoque
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{itensPaginados.map((item) => {
							const cultura = item.produto?.cultura || "-";
							const variedade = item.produto?.variedade
								? ` - ${item.produto.variedade}`
								: "";
							const produtoDescricao = `${cultura}${variedade}`;
							const unidade = item.unidadeMedida ? ` ${item.unidadeMedida}` : "";

							return (
								<TableRow
									key={item.id}
									sx={{
										"&:hover": {
											bgcolor: "#f9f9f9",
										},
									}}
								>
									<TableCell>
										<Typography variant="body2">{formatDate(item.data)}</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
											{produtoDescricao}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2">{item.qualidade || "-"}</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2">
											{item.codigoRastreio || "-"}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2">
											{item.fornecedores?.length ? item.fornecedores.join(", ") : "-"}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography
											variant="body2"
											sx={{
												fontWeight: 600,
												color: item.quantidade <= 0 ? "#ff4d4d" : "#1a9e8f",
											}}
										>
											{formatNumber(item.quantidade)}
											{unidade}
										</Typography>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={itens.length}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				labelRowsPerPage="Linhas por página:"
				labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
				sx={{
					borderTop: "1px solid #e0e0e0",
					".MuiTablePagination-toolbar": {
						color: "#666",
					},
				}}
			/>
		</>
	);
}

export default EstoqueEntradas;
